import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  Check,
  ChevronDown,
  Info,
  SlidersHorizontal,
  X,
} from "lucide-react";
import Dropdown from "../Shared/Dropdown";
import {
  catalogCategories,
  difficultyScale,
  sortOptions,
  type CertificateSort,
  type DifficultyScore,
} from "./catalog";
import type { RoadmapLanguage } from "./types";

type CredentialLibraryFiltersProps = {
  selectedCategoryIds: string[];
  selectedDifficultyScores: DifficultyScore[];
  selectedSort: CertificateSort;
  resultCount: number;
  language: RoadmapLanguage;
  onToggleCategory: (categoryId: string) => void;
  onClearCategories: () => void;
  onToggleDifficulty: (score: DifficultyScore) => void;
  onClearDifficulties: () => void;
  onClearAll: () => void;
  onSortChange: (sort: CertificateSort) => void;
};

const copy = {
  en: {
    all: "All",
    filter: "Filter",
    categoriesAria: "Certificate categories",
    categories: "Categories",
    difficultyLevels: "Difficulty",
    allDifficulties: "All difficulties",
    sort: "Sort",
    sortAria: "Sort certifications",
    difficultyAria: "About the difficulty scale",
    difficulty:
      "Difficulty is an editorial comparison for this website, not an official rating from the awarding bodies.",
    filterTitle: "Filter certifications",
    filterDescription: "Select categories and difficulty levels",
    close: "Close filters",
    clear: "Clear filters",
    showResults: (count: number) =>
      `Show ${count} ${count === 1 ? "result" : "results"}`,
  },
  ar: {
    all: "الكل",
    filter: "فلترة",
    categoriesAria: "مجالات الشهادات",
    categories: "المجالات",
    difficultyLevels: "الصعوبة",
    allDifficulties: "كل المستويات",
    sort: "الترتيب",
    sortAria: "ترتيب الشهادات",
    difficultyAria: "حول مقياس الصعوبة",
    difficulty:
      "تقييم الصعوبة تحريري للمقارنة داخل الموقع، وليس تقييمًا رسميًا صادرًا عن الجهات المانحة.",
    filterTitle: "فلترة الشهادات",
    filterDescription: "اختر المجالات ومستويات الصعوبة",
    close: "إغلاق الفلاتر",
    clear: "مسح الفلاتر",
    showResults: (count: number) => `عرض ${count} نتيجة`,
  },
} as const;

const DifficultyInfo = ({
  language,
  tooltipId,
}: {
  language: RoadmapLanguage;
  tooltipId: string;
}) => {
  const text = copy[language];

  return (
    <div className="group relative shrink-0">
      <button
        type="button"
        aria-label={text.difficultyAria}
        aria-describedby={tooltipId}
        className="flex size-9 items-center justify-center rounded-full border border-black/10 text-lightGray transition hover:border-secondary/40 hover:text-secondary focus:outline-none focus:ring-2 focus:ring-secondary/30"
      >
        <Info size={16} />
      </button>
      <div
        id={tooltipId}
        role="tooltip"
        className="pointer-events-none absolute end-0 top-11 z-30 hidden w-72 rounded-xl bg-primary p-3 text-start text-xs font-medium leading-5 text-white shadow-xl group-focus-within:block group-hover:block"
      >
        {text.difficulty}
      </div>
    </div>
  );
};

const CredentialLibraryFilters = ({
  selectedCategoryIds,
  selectedDifficultyScores,
  selectedSort,
  resultCount,
  language,
  onToggleCategory,
  onClearCategories,
  onToggleDifficulty,
  onClearDifficulties,
  onClearAll,
  onSortChange,
}: CredentialLibraryFiltersProps) => {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const allSelected = selectedCategoryIds.length === 0;
  const allDifficultiesSelected = selectedDifficultyScores.length === 0;
  const activeFilterCount =
    selectedCategoryIds.length + selectedDifficultyScores.length;
  const text = copy[language];
  const selectedSortOption =
    sortOptions.find((option) => option.value === selectedSort) ??
    sortOptions[0];
  const categoryLabel = (category: (typeof catalogCategories)[number]) =>
    category[language === "ar" ? "labelAr" : "labelEn"];
  const sortLabel = (option: (typeof sortOptions)[number]) =>
    option[language === "ar" ? "labelAr" : "labelEn"];
  const difficultyScores: DifficultyScore[] = [1, 2, 3, 4, 5];
  const difficultyLabel = (score: DifficultyScore) =>
    difficultyScale[`${score}`][
      language === "ar" ? "labelAr" : "labelEn"
    ];

  return (
    <>
      <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div
          className="hidden flex-1 flex-wrap gap-2 lg:flex"
          role="group"
          aria-label={text.categoriesAria}
        >
          <button
            type="button"
            aria-pressed={allSelected}
            onClick={onClearCategories}
            className={`rounded-full border px-4 py-2 text-sm font-bold transition ${
              allSelected
                ? "border-primary bg-primary text-white"
                : "border-black/10 bg-white text-primary hover:border-primary/40"
            }`}
          >
            {text.all}
          </button>
          {catalogCategories.map((category) => {
            const selected = selectedCategoryIds.includes(category.id);
            return (
              <button
                key={category.id}
                type="button"
                aria-pressed={selected}
                onClick={() => onToggleCategory(category.id)}
                className={`rounded-full border px-4 py-2 text-start text-xs font-semibold leading-5 transition ${
                  selected
                    ? "border-secondary bg-secondary/10 text-primary"
                    : "border-black/10 bg-white text-primary hover:border-secondary/40"
                }`}
              >
                {categoryLabel(category)}
              </button>
            );
          })}
        </div>

        <div className="flex items-end justify-between gap-3 lg:w-[330px] lg:shrink-0 lg:justify-end">
          <button
            type="button"
            onClick={() => setMobileFiltersOpen(true)}
            className="flex h-11 items-center gap-2 rounded-xl border border-black/10 bg-white px-4 text-sm font-bold text-primary lg:hidden"
          >
            <SlidersHorizontal size={17} />
            {text.filter}
            {activeFilterCount > 0 && (
              <span className="flex size-5 items-center justify-center rounded-full bg-secondary text-[11px] text-white">
                {activeFilterCount}
              </span>
            )}
          </button>

          <div className="flex min-w-0 flex-1 items-center gap-2 lg:max-w-[330px]">
            <Dropdown
              className="min-w-0 flex-1"
              title={text.sort}
              indicator={false}
              orientation="end"
              shape="curved"
              closeOnSelect
              toggleAriaLabel={text.sortAria}
              toggleClassNames="!h-11 !w-full !justify-between !rounded-xl !px-3 !text-start !text-sm !font-semibold !text-primary"
              menuClassNames="!end-0 !top-[3.25rem] !w-[min(320px,calc(100vw-2rem))]"
              toggleImage={
                <>
                  <span className="min-w-0 truncate">
                    {sortLabel(selectedSortOption)}
                  </span>
                  <ChevronDown size={16} className="shrink-0" />
                </>
              }
            >
              <div role="menu" className="space-y-1 px-2">
                {sortOptions.map((option) => {
                  const selected = option.value === selectedSort;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="menuitemradio"
                      aria-checked={selected}
                      onClick={() => onSortChange(option.value)}
                      className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-start text-sm font-semibold transition ${
                        selected
                          ? "bg-secondary/10 text-secondary"
                          : "text-primary hover:bg-[#f5f7f6]"
                      }`}
                    >
                      <span>{sortLabel(option)}</span>
                      {selected && <Check size={16} />}
                    </button>
                  );
                })}
              </div>
            </Dropdown>
          </div>
        </div>
      </div>

      <div
        className="mb-8 hidden flex-wrap items-center gap-2 lg:flex"
        role="group"
        aria-label={text.difficultyLevels}
      >
        <span className="me-1 text-xs font-bold uppercase tracking-[0.1em] text-lightGray">
          {text.difficultyLevels}
        </span>
        <DifficultyInfo
          language={language}
          tooltipId="difficulty-scale-tooltip-desktop"
        />
        <button
          type="button"
          aria-pressed={allDifficultiesSelected}
          onClick={onClearDifficulties}
          className={`rounded-full border px-4 py-2 text-xs font-bold transition ${
            allDifficultiesSelected
              ? "border-primary bg-primary text-white"
              : "border-black/10 bg-white text-primary hover:border-primary/40"
          }`}
        >
          {text.allDifficulties}
        </button>
        {difficultyScores.map((score) => {
          const selected = selectedDifficultyScores.includes(score);
          return (
            <button
              key={score}
              type="button"
              aria-pressed={selected}
              onClick={() => onToggleDifficulty(score)}
              className={`rounded-full border px-4 py-2 text-xs font-bold transition ${
                selected
                  ? "border-secondary bg-secondary/10 text-primary"
                  : "border-black/10 bg-white text-primary hover:border-secondary/40"
              }`}
            >
              {score} · {difficultyLabel(score)}
            </button>
          );
        })}
      </div>

      <Transition appear show={mobileFiltersOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[99999999999] lg:hidden"
          onClose={setMobileFiltersOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-primary/55 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-end" dir={language === "ar" ? "rtl" : "ltr"}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="translate-y-full"
              enterTo="translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="translate-y-0"
              leaveTo="translate-y-full"
            >
              <Dialog.Panel className="max-h-[85vh] w-full overflow-y-auto rounded-t-[28px] bg-white p-5 shadow-2xl">
                <div className="mb-5 flex items-start justify-between gap-4">
                  <div>
                    <Dialog.Title className="text-xl font-bold text-primary">
                      {text.filterTitle}
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-lightGray">
                      {text.filterDescription}
                    </p>
                  </div>
                  <button
                    type="button"
                    aria-label={text.close}
                    onClick={() => setMobileFiltersOpen(false)}
                    className="flex size-10 items-center justify-center rounded-full bg-[#edf0ee] text-primary"
                  >
                    <X size={18} />
                  </button>
                </div>

                <h3 className="mb-2 text-sm font-bold text-primary">
                  {text.categories}
                </h3>
                <div className="space-y-2">
                  <button
                    type="button"
                    aria-pressed={allSelected}
                    onClick={onClearCategories}
                    className={`flex w-full items-center gap-3 rounded-2xl border p-3 text-start transition ${
                      allSelected
                        ? "border-primary bg-primary/5"
                        : "border-black/10"
                    }`}
                  >
                    <span
                      className={`flex size-6 shrink-0 items-center justify-center rounded-md border ${
                        allSelected
                          ? "border-primary bg-primary text-white"
                          : "border-black/20 text-transparent"
                      }`}
                    >
                      <Check size={14} />
                    </span>
                    <span className="text-sm font-bold text-primary">
                      {text.all}
                    </span>
                  </button>

                  {catalogCategories.map((category) => {
                    const selected = selectedCategoryIds.includes(category.id);
                    return (
                      <label
                        key={category.id}
                        className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-3 transition ${
                          selected
                            ? "border-secondary bg-secondary/10"
                            : "border-black/10"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => onToggleCategory(category.id)}
                          className="sr-only"
                        />
                        <span
                          className={`flex size-6 shrink-0 items-center justify-center rounded-md border ${
                            selected
                              ? "border-secondary bg-secondary text-white"
                              : "border-black/20 text-transparent"
                          }`}
                        >
                          <Check size={14} />
                        </span>
                        <span className="min-w-0 flex-1 text-start text-sm font-semibold text-primary">
                          {categoryLabel(category)}
                        </span>
                      </label>
                    );
                  })}
                </div>

                <div className="mt-6 flex items-center gap-2">
                  <h3 className="text-sm font-bold text-primary">
                    {text.difficultyLevels}
                  </h3>
                  <DifficultyInfo
                    language={language}
                    tooltipId="difficulty-scale-tooltip-mobile"
                  />
                </div>
                <div className="mt-2 space-y-2">
                  <button
                    type="button"
                    aria-pressed={allDifficultiesSelected}
                    onClick={onClearDifficulties}
                    className={`flex w-full items-center gap-3 rounded-2xl border p-3 text-start transition ${
                      allDifficultiesSelected
                        ? "border-primary bg-primary/5"
                        : "border-black/10"
                    }`}
                  >
                    <span
                      className={`flex size-6 shrink-0 items-center justify-center rounded-md border ${
                        allDifficultiesSelected
                          ? "border-primary bg-primary text-white"
                          : "border-black/20 text-transparent"
                      }`}
                    >
                      <Check size={14} />
                    </span>
                    <span className="text-sm font-bold text-primary">
                      {text.allDifficulties}
                    </span>
                  </button>

                  {difficultyScores.map((score) => {
                    const selected = selectedDifficultyScores.includes(score);
                    return (
                      <label
                        key={score}
                        className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-3 transition ${
                          selected
                            ? "border-secondary bg-secondary/10"
                            : "border-black/10"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => onToggleDifficulty(score)}
                          className="sr-only"
                        />
                        <span
                          className={`flex size-6 shrink-0 items-center justify-center rounded-md border ${
                            selected
                              ? "border-secondary bg-secondary text-white"
                              : "border-black/20 text-transparent"
                          }`}
                        >
                          <Check size={14} />
                        </span>
                        <span className="min-w-0 flex-1 text-start text-sm font-semibold text-primary">
                          {score} · {difficultyLabel(score)}
                        </span>
                      </label>
                    );
                  })}
                </div>

                <div className="sticky bottom-0 mt-5 flex gap-3 bg-white pt-3">
                  <button
                    type="button"
                    onClick={onClearAll}
                    disabled={allSelected && allDifficultiesSelected}
                    className="flex-1 rounded-xl border border-black/10 px-4 py-3 text-sm font-bold text-primary disabled:opacity-40"
                  >
                    {text.clear}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMobileFiltersOpen(false)}
                    className="flex-1 rounded-xl bg-secondary px-4 py-3 text-sm font-bold text-white"
                  >
                    {text.showResults(resultCount)}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default CredentialLibraryFilters;
